import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnboardingAnswers, createEmptyIncomeSource } from '../utils/onboardingCategoryBuilder';
import {
  BudgetSummary,
  computeBudgetSummary,
  formatBudgetAmount
} from '../utils/onboardingBudgetSummary';
import { ExchangeRates, fetchLatestExchangeRates } from '../utils/exchangeRates';

interface OnboardingBudgetSummaryProps {
  answers: OnboardingAnswers;
  budgetCurrency: string;
  onChange: (answers: OnboardingAnswers) => void;
  onGoToIncome?: () => void;
}

const OnboardingBudgetSummary: React.FC<OnboardingBudgetSummaryProps> = ({
  answers,
  budgetCurrency,
  onChange,
  onGoToIncome
}) => {
  const { t } = useTranslation();
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  const [ratesLoading, setRatesLoading] = useState(false);

  const currency = budgetCurrency || answers.primaryCurrency || 'USD';

  useEffect(() => {
    let cancelled = false;
    setRatesLoading(true);
    void fetchLatestExchangeRates(currency).then(rates => {
      if (!cancelled) {
        setExchangeRates(rates);
        setRatesLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currency]);

  const summary: BudgetSummary = useMemo(
    () => computeBudgetSummary(answers, currency, exchangeRates),
    [answers, currency, exchangeRates]
  );

  const handleGoToIncome = () => {
    onGoToIncome?.();
    window.requestAnimationFrame(() => onGoToIncome?.());
  };

  const handleAddIncome = () => {
    onChange({
      ...answers,
      incomeSources: [
        ...answers.incomeSources,
        createEmptyIncomeSource(answers.incomeSources.length, currency)
      ]
    });
    handleGoToIncome();
  };

  const balanceClass =
    summary.isOverBudget ? 'budget-summary-balance over' : 'budget-summary-balance';

  return (
    <aside className="budget-summary-panel" aria-live="polite">
      <h4>{t('builder.budgetSummary.title', { ns: 'onboarding' })}</h4>
      <p className="budget-summary-hint">
        {t('builder.budgetSummary.hint', { ns: 'onboarding' })}
      </p>

      <dl className="budget-summary-stats">
        <div className="budget-summary-row">
          <dt>{t('builder.budgetSummary.totalIncome', { ns: 'onboarding' })}</dt>
          <dd>
            {formatBudgetAmount(summary.totalIncome)} {summary.currency}
          </dd>
        </div>
        <div className="budget-summary-row">
          <dt>{t('builder.budgetSummary.totalCosts', { ns: 'onboarding' })}</dt>
          <dd>
            {formatBudgetAmount(summary.totalCosts)} {summary.currency}
          </dd>
        </div>
        <div className="budget-summary-row budget-summary-row-balance">
          <dt>{t('builder.budgetSummary.balance', { ns: 'onboarding' })}</dt>
          <dd className={balanceClass}>
            {formatBudgetAmount(summary.balance)} {summary.currency}
          </dd>
        </div>
      </dl>

      {ratesLoading && (
        <p className="budget-summary-loading">
          {t('builder.budgetSummary.loadingRates', { ns: 'onboarding' })}
        </p>
      )}

      {summary.isOverBudget && (
        <div className="budget-summary-alert" role="status">
          <button
            type="button"
            className="budget-summary-alert-title"
            onClick={handleGoToIncome}
          >
            {t('builder.budgetSummary.zeroBalanceTitle', { ns: 'onboarding' })}
          </button>
          <p>{t('builder.budgetSummary.zeroBalanceBody', { ns: 'onboarding' })}</p>
          <p>{t('builder.budgetSummary.incomeSourcesHint', { ns: 'onboarding' })}</p>
          <button type="button" className="builder-add budget-summary-add-income" onClick={handleAddIncome}>
            + {t('builder.sections.income.add', { ns: 'onboarding' })}
          </button>
        </div>
      )}
    </aside>
  );
};

export default OnboardingBudgetSummary;
